import type { SessionInfo, User } from '@panelvault/shared';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { api, ApiError } from '../../lib/api';
import { clearApiCache } from '../../lib/pwa-caches';

export const sessionQueryKey = ['session', 'me'] as const;

/**
 * Current user, or null when nobody is logged in. A 401 is a valid answer here,
 * not an error, so it must not trigger retries.
 */
export function useCurrentUser(): UseQueryResult<User | null> {
  return useQuery({
    queryKey: sessionQueryKey,
    queryFn: async () => {
      try {
        return await api.get<User>('/auth/me');
      } catch (error) {
        if (error instanceof ApiError && error.isUnauthorized) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 60_000,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (credentials: { username: string; password: string }) =>
      api.post<SessionInfo>('/auth/login', credentials),
    onSuccess: (session) => {
      queryClient.setQueryData(sessionQueryKey, session.user);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: true }>('/auth/logout'),
    onSuccess: () => {
      queryClient.setQueryData(sessionQueryKey, null);
      queryClient.clear();
      void clearApiCache();
    },
  });
}
